import React, { useState, useEffect, useRef } from 'react';
import { paymentAPI, initializeTapPayment, loadTapSDK, CustomerInfo, formatCurrency, PaymentMethod } from '../../services/paymentAPI';

interface PaymentFormProps {
  amount: number;
  currency?: string;
  invoiceId?: number;
  customer: CustomerInfo;
  onSuccess: (paymentResult: any) => void;
  onError: (error: any) => void;
  onCancel?: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  currency = 'KWD',
  invoiceId,
  customer,
  onSuccess,
  onError,
  onCancel,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [showCardForm, setShowCardForm] = useState(false);
  const tapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const response = await paymentAPI.getPaymentMethods();
      setPaymentMethods(response.data || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    }
  };

  const handlePaymentLinkGeneration = async () => {
    if (!invoiceId) {
      onError({ message: 'Invoice ID is required for payment link generation' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await paymentAPI.createPaymentLink({
        invoice_id: invoiceId,
        amount,
        currency,
        customer,
        description: `Payment for Invoice #${invoiceId}`,
      });

      if (response.data.payment_url) {
        // Open payment URL in new window
        window.open(response.data.payment_url, '_blank');
        onSuccess(response.data);
      } else {
        onError({ message: 'Failed to generate payment link' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || (error as any)?.message || 'Payment failed';
      onError({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardPayment = async () => {
    setIsLoading(true);
    setShowCardForm(true);

    try {
      // Load TAP SDK and initialize payment form
      await loadTapSDK();
      
      if (tapContainerRef.current) {
        await initializeTapPayment(tapContainerRef.current.id, {
          amount,
          currency,
          customer,
          onSuccess: async (tapResponse: any) => {
            try {
              // Process the payment with our backend
              const response = await paymentAPI.processCardPayment({
                amount,
                currency,
                invoice_id: invoiceId,
                customer_id: customer.id || 0,
                source_id: tapResponse.id,
                description: `Payment for Invoice #${invoiceId}`,
              });
              
              onSuccess(response.data);
            } catch (error: any) {
              const errorMessage = error.response?.data?.message || (error as any)?.message || 'Card payment failed';
              onError({ message: errorMessage });
            } finally {
              setIsLoading(false);
            }
          },
          onError: (error: any) => {
            onError(error);
            setIsLoading(false);
            setShowCardForm(false);
          },
        });
      }
    } catch (error) {
      onError(error);
      setIsLoading(false);
      setShowCardForm(false);
    }
  };

  const renderPaymentMethodButton = (method: string, label: string, icon?: string) => (
    <button
      key={method}
      onClick={() => setSelectedMethod(method)}
      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
        selectedMethod === method
          ? 'border-blue-500 bg-blue-50 text-blue-700'
          : 'border-gray-300 hover:border-gray-400'
      }`}
    >
      <div className="flex items-center space-x-3">
        {icon && <img src={icon} alt={label} className="w-8 h-8" />}
        <span className="font-medium">{label}</span>
      </div>
      <div className={`w-4 h-4 rounded-full border-2 ${
        selectedMethod === method ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
      }`}>
        {selectedMethod === method && (
          <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
        )}
      </div>
    </button>
  );

  if (showCardForm) {
    return (
      <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Complete Payment
          </h2>
          <p className="text-gray-600">
            Amount: {formatCurrency(amount, currency)}
          </p>
        </div>

        {/* TAP Payment Form Container */}
        <div id="tap-payment-form" ref={tapContainerRef} className="mb-6" />

        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <span className="ml-2 text-gray-600">Processing payment...</span>
          </div>
        )}

        <div className="flex space-x-3">
          {onCancel && (
            <button
              onClick={() => {
                setShowCardForm(false);
                onCancel();
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Choose Payment Method
        </h2>
        <p className="text-gray-600">
          Amount: {formatCurrency(amount, currency)}
        </p>
        <p className="text-sm text-gray-500">
          Customer: {customer.name}
        </p>
      </div>

      <div className="space-y-3 mb-6">
        {renderPaymentMethodButton('card', 'Credit/Debit Card', '/icons/card.png')}
        {renderPaymentMethodButton('knet', 'KNET', '/icons/knet.png')}
        {renderPaymentMethodButton('apple_pay', 'Apple Pay', '/icons/apple-pay.png')}
        {renderPaymentMethodButton('google_pay', 'Google Pay', '/icons/google-pay.png')}
        {renderPaymentMethodButton('link', 'Payment Link', '/icons/link.png')}
      </div>

      <div className="space-y-3">
        {selectedMethod === 'link' ? (
          <button
            onClick={handlePaymentLinkGeneration}
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Generating Link...' : 'Generate Payment Link'}
          </button>
        ) : (
          <button
            onClick={handleCardPayment}
            disabled={isLoading}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Loading...' : `Pay with ${selectedMethod.replace('_', ' ').toUpperCase()}`}
          </button>
        )}

        {onCancel && (
          <button
            onClick={onCancel}
            className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
        )}
      </div>

      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>Payments are processed securely through TAP Payments</p>
        <p>Supported in Kuwait and GCC countries</p>
      </div>
    </div>
  );
};

export default PaymentForm;